export const EMPTY_VALUE = '—'

interface DecimalOptions {
  minFractionDigits?: number
  maxFractionDigits?: number
  fallbackValue?: string
  useGrouping?: boolean
}

export const numericUtils = {
  isValidNumericInput(value: number | null | undefined): value is number {
    if (value === null || value === undefined || isNaN(value)) {
      return false
    }

    return true
  },

  isValidCurrencyInput(value: string): boolean {
    if (value === '') return true
    return /^\d+(,\d{0,2})?$/.test(value)
  },

  hasValue(value: string | number | null | undefined): boolean {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value

    if (!this.isValidNumericInput(numericValue)) {
      return false
    }

    return numericValue > 0
  },

  parseBRLDecimalToNumber(value: string | number | null | undefined): number {
    if (typeof value === 'number') return value
    if (!value) return 0

    const cleanString = value.replace(/[^\d,-]/g, '')
    const dotString = cleanString.replace(',', '.')

    const result = parseFloat(dotString)
    return isNaN(result) ? 0 : result
  },

  formatNumberToBRLCurrency(value: string | number | null | undefined, fallbackValue: string = 'R$ 0,00'): string {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value

    if (!this.isValidNumericInput(numericValue)) {
      return fallbackValue
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(numericValue)
  },

  formatNumberToBRLDecimal(value: number | null | undefined, options?: DecimalOptions): string {
    const { minFractionDigits = 2, maxFractionDigits = 2, fallbackValue = '0,00', useGrouping = true } = options || {}

    if (!this.isValidNumericInput(value)) {
      return fallbackValue
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'decimal',
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
      useGrouping,
    }).format(value)
  },

  formatKwh(value: string | number | null | undefined, fallbackValue: string = '0,00'): string {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value

    return this.formatNumberToBRLDecimal(numericValue, {
      minFractionDigits: 2,
      maxFractionDigits: 3,
      fallbackValue,
    })
  },

  formatKw(value: string | number | null | undefined, fallbackValue: string = EMPTY_VALUE): string {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value

    return this.formatNumberToBRLDecimal(numericValue, {
      minFractionDigits: 1,
      maxFractionDigits: 1,
      fallbackValue,
    })
  },

  formatDistinctValues(values: number[], format: (value: number) => string): string {
    if (values.length === 0) return ''

    const sorted = [...values].sort((first, second) => first - second)

    if (sorted.length <= 2) return sorted.map((value) => format(value)).join(', ')

    return `${format(sorted[0])} a ${format(sorted[sorted.length - 1])}`
  },

  formatPercentage(value: string | number | null | undefined, fallbackValue: string = EMPTY_VALUE): string {
    const numericValue = typeof value === 'string' ? parseFloat(value) : value

    return this.formatNumberToBRLDecimal(numericValue, {
      minFractionDigits: 0,
      maxFractionDigits: 0,
      fallbackValue,
    })
  },
}
